import cx from 'classnames'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'

import {
  CHART_HEIGHT,
  COLORS,
  convertToPercents,
  formatNumber,
  GREEN,
  LIGHT_ORANGE,
  PINK,
  RED,
  tooltipFormatter,
  tooltipFormatterNumber,
  tooltipFormatterPercent,
  tooltipFundingRateFormatter,
  tooltipLabelFormatter,
  tooltipLabelFormatterUnits,
  yaxisFormatter,
  yaxisFormatterNumber,
  yaxisFormatterPercent,
  YAXIS_MAX_PERCENT,
  YAXIS_MIN_PERCENT,
  YAXIS_WIDTH,
} from '../helpers'

import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import ChartWrapper from '../components/ChartWrapper'
import DateRangeSelect from '../components/DateRangeSelect'
import FeesChart from '../components/FeesChart'
import GenericChart from '../components/GenericChart'
import { TokenVolumeChart, VolumeChart } from '../components/VolumeChart'

import Card from '../components/Card'
import PoolAmountChart from '../components/PoolAmountChart'
import TradersProfitLossChart from '../components/TradersProfitLossChart'
import {
  useAumPerformanceData,
  useBorrowingRateData,
  useFeesData,
  useFundingRateData,
  useLastBlock,
  useLastSubgraphBlock,
  usePlpData,
  usePlpPerformanceData,
  useSwapSources,
  useTokenStats,
  useTradersData,
  useUsersData,
  useVolumeData,
} from '../dataProvider'

import { FIRST_DATE } from '../configs/config'
import { findYDomain } from '../utils'

import { useTokenVolumeData } from '../hooks/useTokenVolumeData'

const NOW = Math.floor(Date.now() / 1000)

function Polygon(props) {
  const DEFAULT_GROUP_PERIOD = 86400
  const [groupPeriod] = useState(DEFAULT_GROUP_PERIOD)

  const defaultFromDate =
    moment().subtract(2, 'month').toDate().getTime() < FIRST_DATE.getTime()
      ? FIRST_DATE
      : moment().subtract(2, 'month').toDate()

  const [dataRange, setDataRange] = useState({
    fromValue: defaultFromDate,
    toValue: null,
  })

  const { mode, chainName } = props

  const from = dataRange.fromValue
    ? Math.floor(+new Date(dataRange.fromValue.setUTCHours(0, 0, 0, 0)) / 1000)
    : undefined
  const to = dataRange.toValue
    ? Math.floor(+new Date(dataRange.toValue) / 1000)
    : NOW

  const params = { from, to, groupPeriod, chainName: chainName }

  const [borrowingRateData, borrowingRateKeys, borrowingRateDataLoading] =
    useBorrowingRateData(params)

  const [
    fundingRateData,
    fundingRateOverrideData,
    fundingRateKeys,
    fundingRateDataLoading,
  ] = useFundingRateData(params)

  const mapTokenLongShortTokens = {
    BTC: {
      LONG: ['BTC'],
      SHORT: ['USDT', 'USDC'],
    },
    ETH: {
      LONG: ['ETH'],
      SHORT: ['USDT', 'USDC'],
    },
    MATIC: {
      LONG: ['MATIC'],
      SHORT: ['USDT', 'USDC'],
    },
  }

  const [performanceFundingFee, overridePerformanceFundingFee] = useMemo(() => {
    if (!borrowingRateData || !fundingRateData) {
      return []
    }

    const data = fundingRateData.map((fundingRate) => {
      const timestamp = fundingRate.timestamp
      const stableKeys = [
        'USDC.short',
        'USDC.long',
        'USDT.short',
        'USDT.long',
        'DAI.short',
        'DAI.long',
      ]
      const _fundingDataKeys = Object.keys(fundingRate).filter(
        (i) => !['timestamp', ...stableKeys].includes(i),
      )

      const borrowingElement = borrowingRateData.find(
        (i) => i.timestamp === timestamp,
      )

      const fundingPerformance = _fundingDataKeys.reduce(
        (memo, tokenKey) => {
          const isLong = tokenKey.includes('.long')
          const _fundingDataKeyRemovedSuffix = tokenKey
            .replace('.short', '')
            .replace('.long', '')

          const tokenData = mapTokenLongShortTokens[
            _fundingDataKeyRemovedSuffix
          ][isLong ? `LONG` : `SHORT`].reduce((accumToken, token) => {
            const fundingWithBorrowing = borrowingElement
              ? borrowingElement[token]
                ? fundingRate[tokenKey] + borrowingElement[token]
                : 0
              : 0

            return {
              ...accumToken,
              [`${tokenKey}.${token}`]: fundingWithBorrowing,
            }
          }, {})

          return {
            ...memo,
            ...tokenData,
          }
        },
        {
          timestamp: timestamp,
        },
      )

      return fundingPerformance
    }, [])

    const overrideData = data.map((each) => {
      const tokenOverrideData = Object.keys(each)
        .filter((key) => key !== 'timestamp')
        .reduce((accum, curr) => {
          return {
            ...accum,
            [curr]:
              each[curr] < YAXIS_MIN_PERCENT ? YAXIS_MIN_PERCENT : each[curr],
          }
        }, {})

      return {
        timestamp: each.timestamp,
        ...tokenOverrideData,
      }
    })

    return [data, overrideData]
  }, [borrowingRateData, fundingRateData])

  const performanceFundingFeeKeys = Object.keys(
    mapTokenLongShortTokens,
  ).flatMap((key) => {
    return [
      ...mapTokenLongShortTokens[key].LONG.map((o) => ({
        key: `${key}.long.${o}`,
      })),
      ...mapTokenLongShortTokens[key].SHORT.map((o) => ({
        key: `${key}.short.${o}`,
      })),
    ]
  })

  const [volumeData, volumeLoading] = useVolumeData(params)

  const [totalVolumeData, totalVolumeLoading] = useVolumeData({
    chainName: chainName,
  })
  const [totalVolume, totalVolumeDelta] = useMemo(() => {
    if (!totalVolumeData) {
      return []
    }
    const total = totalVolumeData[totalVolumeData.length - 1]?.cumulative
    const delta =
      total - totalVolumeData[totalVolumeData.length - 2]?.cumulative
    return [total, delta]
  }, [totalVolumeData])

  const [totalTradingVolume, totalTradingVolumeDelta] = useMemo(() => {
    if (!totalVolumeData) {
      return []
    }
    const total = totalVolumeData[totalVolumeData.length - 1]?.cumulativeMargin
    const delta =
      total - totalVolumeData[totalVolumeData.length - 2]?.cumulativeMargin
    return [total, delta]
  }, [totalVolumeData])

  const [totalPlpVolume, totalPlpVolumeDelta] = useMemo(() => {
    if (!totalVolumeData) {
      return []
    }
    const total =
      totalVolumeData[totalVolumeData.length - 1]?.cumulativeLiquidity
    const delta =
      total - totalVolumeData[totalVolumeData.length - 2]?.cumulativeLiquidity
    return [total, delta]
  }, [totalVolumeData])

  const [feesData, feesLoading] = useFeesData(params)
  const [totalFeesData, totalFeesLoading] = useFeesData({
    chainName: chainName,
  })
  const [totalFees, totalFeesDelta] = useMemo(() => {
    if (!totalFeesData) {
      return []
    }
    const total = totalFeesData[totalFeesData.length - 1]?.cumulative
    const delta = total - totalFeesData[totalFeesData.length - 2]?.cumulative
    return [total, delta]
  }, [totalFeesData])

  const [plpData, plpLoading, plpError, aumPlpSupplyYDomain] =
    usePlpData(params)

  const [totalAum, totalAumDelta] = useMemo(() => {
    if (!plpData) {
      return []
    }
    const total = plpData[plpData.length - 1]?.aum
    const delta = total - plpData[plpData.length - 2]?.aum
    return [total, delta]
  }, [plpData])

  const [aumPerformanceData, aumPerformanceLoading] = useAumPerformanceData(
    params,
    feesData,
    feesLoading,
    plpData,
    plpLoading,
    volumeData,
    volumeLoading,
  )
  const [plpPerformanceData, plpPriceComparisonYDomain, plpPerformanceYDomain] =
    usePlpPerformanceData(plpData, feesData, params)

  const [tradersData, tradersLoading] = useTradersData(params)

  const [swapSources, swapSourcesLoading] = useSwapSources(params)
  const swapSourcesKeys = Object.keys(
    (swapSources || []).reduce((memo, el) => {
      Object.keys(el).forEach((key) => {
        if (key === 'all' || key === 'timestamp') return
        memo[key] = true
      })
      return memo
    }, {}),
  )

  const [usersData, usersLoading] = useUsersData(params)
  const [totalUsers, totalUsersDelta] = useMemo(() => {
    if (!usersData) {
      return [null, null]
    }
    const total = usersData[usersData.length - 1]?.uniqueCountCumulative
    const prevTotal = usersData[usersData.length - 2]?.uniqueCountCumulative
    const delta = total && prevTotal ? total - prevTotal : null
    return [total, delta]
  }, [usersData])

  const [lastSubgraphBlock, , lastSubgraphBlockError] = useLastSubgraphBlock()
  const [lastBlock] = useLastBlock()

  const isObsolete =
    lastSubgraphBlock &&
    lastBlock &&
    lastBlock.timestamp - lastSubgraphBlock.timestamp > 3600

  const [isExperiment, setIsExperiment] = useState(false)
  useEffect(() => {
    setIsExperiment(window.localStorage.getItem('experiment'))
  }, [setIsExperiment])

  const onDateRangeChange = (dates) => {
    const [start, end] = dates
    setDataRange({ fromValue: start, toValue: end })
  }

  const dateRangeOptions = [
    {
      label: 'Last Month',
      id: 1,
    },
    {
      label: 'Last 2 Months',
      id: 2,
      isDefault: true,
    },
    {
      label: 'Last 3 Months',
      id: 3,
    },
    {
      label: 'All time',
      id: 4,
    },
  ]

  const [tokenStatsData, tokenStatsLoading, tokenError, tokenStatKeys] =
    useTokenStats(params)

  const [tokenVolumeData, tokenVolumeLoading] = useTokenVolumeData(params)

  return (
    <div className="Home">
      <div className="page-title-section">
        <div className="page-title-block">
          <h1>PERP88 Analytics</h1>
          {lastSubgraphBlock && lastBlock && (
            <p className={cx('page-description', { warning: isObsolete })}>
              {isObsolete && 'Data is obsolete. '}
              Updated {moment(lastSubgraphBlock.timestamp * 1000).fromNow()}
              &nbsp;at block{' '}
              <a
                rel="noreferrer"
                target="_blank"
                href={`https://polygonscan.com/block/${lastSubgraphBlock.number}`}
              >
                {lastSubgraphBlock.number}
              </a>
            </p>
          )}
          {lastSubgraphBlockError && (
            <p className="page-description warning">
              Subgraph data is temporarily unavailable.
            </p>
          )}
        </div>
        <div className="form">
          <DateRangeSelect
            options={dateRangeOptions}
            startDate={dataRange.fromValue}
            endDate={dataRange.toValue}
            onChange={onDateRangeChange}
          />
        </div>
      </div>

      <div className="my-3 grid gap-3 md:my-10 md:grid-cols-3 md:gap-6 ">
        <Card
          loading={!!!totalVolume || !!!totalVolumeData}
          title="Total Volume"
          value={totalVolume}
          rawChanged24Hrs={totalVolumeDelta}
          displayChanged24Hrs={
            totalVolumeData &&
            formatNumber(totalVolumeDelta, {
              currency: true,
            })
          }
        />

        <Card
          loading={!!!totalVolume}
          title="Total PLP Volume"
          value={totalPlpVolume}
          rawChanged24Hrs={totalPlpVolumeDelta}
          displayChanged24Hrs={
            totalPlpVolumeDelta &&
            formatNumber(totalPlpVolumeDelta, {
              currency: true,
            })
          }
        />

        <Card
          loading={!!!totalVolume}
          title="Total Trading Volume"
          value={totalTradingVolume}
          rawChanged24Hrs={totalTradingVolumeDelta}
          displayChanged24Hrs={
            totalTradingVolumeDelta &&
            formatNumber(totalTradingVolumeDelta, {
              currency: true,
            })
          }
        />

        <Card
          loading={!!!totalFees}
          title="Total Fees"
          value={totalFees}
          rawChanged24Hrs={totalFeesDelta}
          displayChanged24Hrs={
            totalFeesDelta &&
            formatNumber(totalFeesDelta, {
              currency: true,
            })
          }
        />
        <Card
          loading={!!!totalAum || !!!totalAumDelta}
          title="PLP Pool"
          value={totalAum}
          rawChanged24Hrs={totalAumDelta}
          displayChanged24Hrs={
            totalAumDelta &&
            formatNumber(totalAumDelta, {
              currency: true,
            })
          }
        />

        <Card
          loading={!!!totalUsers}
          title="Total Users"
          value={totalUsers}
          rawChanged24Hrs={totalUsersDelta}
          displayChanged24Hrs={
            !isNaN(parseFloat(totalUsersDelta)) && totalUsersDelta.toFixed(0)
          }
          isCurrency={false}
        />
      </div>

      <div className="chart-grid">
        {/* <div className="chart-cell stats"></div>
        <div className="chart-cell stats"></div>
        <div className="chart-cell stats"></div>
        <div className="chart-cell stats"></div> */}

        {/* <div className="chart-cell stats">
          {openInterest ? <>
            <div className="total-stat-label">Open Interest</div>
            <div className="total-stat-value">
              {formatNumber(openInterest, { currency: true })}
              <span className={cx("total-stat-delta", (openInterestDelta > 0 ? 'plus' : 'minus'))} title="Change since previous day">
                {openInterestDelta > 0 ? '+' : ''}{formatNumber(openInterestDelta, { currency: true, compact: true })}
              </span>
            </div>
          </> : (tradersLoading ? <RiLoader5Fill size="3em" className="loader" /> : null)}
        </div> */}
        <div className="chart-cell">
          <VolumeChart
            data={volumeData}
            loading={volumeLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
            xaxisTickFormatter={tooltipLabelFormatter}
            yaxisTickFormatter={yaxisFormatter}
            tooltipLabelFormatter={tooltipLabelFormatter}
            tooltipFormatter={tooltipFormatter}
          />
        </div>

        <div className="chart-cell">
          <TokenVolumeChart
            data={tokenVolumeData}
            loading={tokenVolumeLoading}
            chartHeight={CHART_HEIGHT}
            params={params}
          />
        </div>

        <div className="chart-cell">
          <FeesChart
            data={feesData}
            loading={feesLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
          />
        </div>
        <div className="chart-cell">
          <ChartWrapper
            title="AUM & PLP Supply"
            loading={plpLoading}
            data={plpData}
            csvFields={[{ key: 'aum' }, { key: 'plpSupply' }]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={plpData} syncId="syncPlp">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={tooltipLabelFormatter}
                  minTickGap={30}
                />
                <YAxis
                  dataKey="plpSupply"
                  tickFormatter={yaxisFormatter}
                  domain={aumPlpSupplyYDomain}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatterNumber}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  strokeWidth={2}
                  unit="$"
                  dot={false}
                  dataKey="aum"
                  stackId="a"
                  name="AUM"
                  stroke={COLORS[0]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  strokeWidth={2}
                  dot={false}
                  dataKey="plpSupply"
                  stackId="a"
                  name="PLP Supply"
                  stroke={COLORS[1]}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartWrapper>
        </div>
        <div className="chart-cell">
          <PoolAmountChart
            tokenStatsData={tokenStatsData}
            tokenStatsLoading={tokenStatsLoading}
            tokenStatsItem={tokenStatKeys}
            syncId="syncPlp"
          />
        </div>

        <div className="chart-cell">
          <ChartWrapper
            title="PLP Performance"
            loading={plpLoading}
            data={plpPerformanceData}
            csvFields={[
              { key: 'performanceSyntheticCollectedFees' },
              { key: 'performanceLpBtcCollectedFees' },
              { key: 'performanceLpEthCollectedFees' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={plpPerformanceData} syncId="syncPlp">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={tooltipLabelFormatter}
                  minTickGap={30}
                />
                <YAxis
                  dataKey="performanceSyntheticCollectedFees"
                  domain={plpPerformanceYDomain}
                  unit="%"
                  tickFormatter={yaxisFormatterNumber}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatterNumber}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Line
                  dot={false}
                  isAnimationActive={false}
                  type="monotone"
                  unit="%"
                  dataKey="performanceLpBtcCollectedFees"
                  name="% LP BTC-USDC"
                  stroke={COLORS[2]}
                />
                <Line
                  dot={false}
                  isAnimationActive={false}
                  type="monotone"
                  unit="%"
                  dataKey="performanceLpEthCollectedFees"
                  name="% LP ETH-USDC"
                  stroke={COLORS[4]}
                />
                <Line
                  dot={false}
                  isAnimationActive={false}
                  type="monotone"
                  unit="%"
                  dataKey="performanceSyntheticCollectedFees"
                  name="% Index"
                  stroke={COLORS[0]}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>
                <span style={{ color: COLORS[0] }}>% of Index</span> is PLP with
                fees / Index Price * 100. Index is a basket 25% BTC, 25% ETH and
                50% USDC rebalanced once&nbsp;a&nbsp;day
                <br />
                <span style={{ color: COLORS[4] }}>% of LP TOKEN-USDC</span> is
                PLP Price with fees / LP TOKEN-USDC * 100
                <br />
              </p>
            </div>
          </ChartWrapper>
        </div>
        <div className="chart-cell">
          <ChartWrapper
            title="PLP Price Comparison"
            loading={plpLoading}
            data={plpPerformanceData}
            csvFields={[
              { key: 'syntheticPrice' },
              { key: 'plpPrice' },
              { key: 'plpPlusFees' },
              { key: 'lpBtcPrice' },
              { key: 'lpEthPrice' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={plpPerformanceData} syncId="syncPlp">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={tooltipLabelFormatter}
                  minTickGap={30}
                />
                <YAxis
                  dataKey="plpPrice"
                  domain={plpPriceComparisonYDomain}
                  tickFormatter={yaxisFormatterNumber}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatterNumber}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />

                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="syntheticPrice"
                  name="Index Price"
                  stroke={PINK}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="plpPrice"
                  name="PLP Price"
                  stroke={COLORS[1]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={2}
                  dot={false}
                  dataKey="plpPlusFees"
                  name="PLP w/ fees"
                  stroke={COLORS[3]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="lpBtcPrice"
                  name="LP BTC-USDC"
                  stroke={COLORS[2]}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  unit="$"
                  strokeWidth={1}
                  dot={false}
                  dataKey="lpEthPrice"
                  name="LP ETH-USDC"
                  stroke={COLORS[4]}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>
                <span style={{ color: COLORS[3] }}>PLP with fees</span> is based
                on PLP share of fees received
                <br />
                <span style={{ color: PINK }}>Index Price</span> is a basket of
                25% BTC, 25% ETH, 50% USDC rebalanced once &nbsp;a&nbsp;day
              </p>
            </div>
          </ChartWrapper>
        </div>
        <div className="chart-cell">
          <ChartWrapper
            title="Traders Net PnL"
            loading={tradersLoading}
            data={tradersData?.data}
            csvFields={[
              { key: 'pnl', name: 'Net PnL' },
              { key: 'pnlCumulative', name: 'Cumulative PnL' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <ComposedChart data={tradersData?.data} syncId="tradersId">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={tooltipLabelFormatter}
                  minTickGap={30}
                />
                <YAxis
                  domain={[
                    -tradersData?.stats.maxAbsCumulativePnl * 1.05,
                    tradersData?.stats.maxAbsCumulativePnl * 1.05,
                  ]}
                  orientation="right"
                  yAxisId="right"
                  tickFormatter={yaxisFormatter}
                  width={YAXIS_WIDTH}
                  tick={{ fill: COLORS[4] }}
                />
                <YAxis
                  domain={[
                    -tradersData?.stats.maxAbsPnl * 1.05,
                    tradersData?.stats.maxAbsPnl * 1.05,
                  ]}
                  tickFormatter={yaxisFormatter}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatter}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Bar
                  type="monotone"
                  fill={mode == 'dark' ? '#FFFFFF' : '#000000'}
                  dot={false}
                  dataKey="pnl"
                  name="Net PnL"
                >
                  {(tradersData?.data || []).map((item, i) => {
                    return (
                      <Cell
                        key={`cell-${i}`}
                        fill={item.pnl > 0 ? '#22c761' : '#f93333'}
                      />
                    )
                  })}
                </Bar>
                <Line
                  type="monotone"
                  strokeWidth={2}
                  stroke={COLORS[4]}
                  dataKey="currentPnlCumulative"
                  name="Cumulative PnL"
                  yAxisId="right"
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>Fees are not factored into PnL</p>
            </div>
          </ChartWrapper>
        </div>
        <div className="chart-cell">
          <TradersProfitLossChart
            syncId="tradersId"
            loading={tradersLoading}
            tradersData={tradersData}
            yaxisWidth={YAXIS_WIDTH}
            chartHeight={CHART_HEIGHT}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={borrowingRateDataLoading}
            title="Borrowing Rate Annualized"
            syncId="borrowing"
            data={borrowingRateData}
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFormatterPercent}
            items={borrowingRateKeys}
            type="Line"
            yaxisDomain={
              borrowingRateData ? findYDomain(borrowingRateData) : null
            }
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={tokenStatsLoading}
            title="Asset Utilization Chart"
            syncId="borrowing"
            data={tokenStatsData ? tokenStatsData.assetUtilize : null}
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFormatterPercent}
            items={tokenStatKeys}
            type="Line"
            yaxisDomain={
              tokenStatsData ? findYDomain(tokenStatsData.assetUtilize) : null
            }
          />
        </div>

        <div className="chart-cell">
          <GenericChart
            loading={fundingRateDataLoading}
            title="Funding Rate Annualized"
            data={fundingRateData}
            overrideData={fundingRateOverrideData}
            syncId="borrowing"
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFundingRateFormatter}
            tooltipTypeFormatter="OVERRIDE_CHART"
            items={fundingRateKeys}
            type="Line"
            yaxisDomain={
              fundingRateData
                ? findYDomain(fundingRateData, {
                    minLimit: YAXIS_MIN_PERCENT,
                    maxLimit: YAXIS_MAX_PERCENT,
                  })
                : null
            }
          />
          <div className="chart-description">
            <p>
              A negative Funding Fee means traders are earning while a positive
              Funding Fee means traders are paying.
            </p>
          </div>
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={fundingRateDataLoading || borrowingRateDataLoading}
            title="Net Borrowing & Funding Rate Annualized"
            data={performanceFundingFee}
            overrideData={overridePerformanceFundingFee}
            syncId="borrowing"
            yaxisTickFormatter={yaxisFormatterPercent}
            tooltipFormatter={tooltipFundingRateFormatter}
            tooltipTypeFormatter="OVERRIDE_CHART"
            items={performanceFundingFeeKeys}
            type="Line"
            yaxisDomain={findYDomain(performanceFundingFee, {
              minLimit: YAXIS_MIN_PERCENT,
              maxLimit: YAXIS_MAX_PERCENT,
            })}
          />
          <div className="chart-description">
            <p>
              A negative Funding Fee means traders are earning while a positive
              Funding Fee means traders are paying.
            </p>
          </div>
        </div>

        <div className="chart-cell">
          <GenericChart
            loading={tradersLoading}
            title="Open Interest"
            data={tradersData?.data.map((item) => ({
              all: item.openInterest,
              ...item,
            }))}
            controls={{
              convertToPercents: convertToPercents,
            }}
            yaxisDataKey="all"
            items={[
              { key: 'shortOpenInterest', name: 'Short', color: RED },
              { key: 'longOpenInterest', name: 'Long', color: GREEN },
            ]}
            type="Bar"
          />
        </div>
        {
          <div className="chart-cell">
            <GenericChart
              syncId="syncPlp"
              loading={aumPerformanceLoading}
              title="AUM Performance Annualized"
              data={aumPerformanceData}
              yaxisDataKey="apr"
              yaxisTickFormatter={yaxisFormatterPercent}
              tooltipFormatter={tooltipFormatterPercent}
              items={[{ key: 'apr', name: 'APR', color: COLORS[0] }]}
              description="Formula = Daily Fees / PLP Pool * 365 days * 100"
              type="Composed"
            />
          </div>
        }
        {
          <div className="chart-cell">
            <GenericChart
              syncId="syncPlp"
              loading={aumPerformanceLoading}
              title="AUM Daily Usage"
              data={aumPerformanceData}
              yaxisDataKey="usage"
              yaxisTickFormatter={yaxisFormatterPercent}
              tooltipFormatter={tooltipFormatterPercent}
              items={[{ key: 'usage', name: 'Daily Usage', color: COLORS[4] }]}
              description="Formula = Daily Volume / PLP Pool * 100"
              type="Composed"
            />
          </div>
        }
        <div className="chart-cell">
          <GenericChart
            syncId="syncPlp"
            loading={usersLoading}
            title="Unique Users"
            data={usersData}
            truncateYThreshold={6500}
            yaxisDataKey="uniqueSum"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'uniqueSwapCount', name: 'Swaps' },
              { key: 'uniqueMarginCount', name: 'Margin trading' },
              { key: 'uniqueMintBurnCount', name: 'Mint & Burn PLP' },
            ]}
            type="Composed"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            syncId="syncPlp"
            loading={usersLoading}
            title="New Users"
            data={usersData?.map((item) => ({ ...item, all: item.newCount }))}
            truncateYThreshold={6000}
            yaxisDataKey="newCount"
            rightYaxisDataKey="uniqueCountCumulative"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'newSwapCount', name: 'Swap' },
              { key: 'newMarginCount', name: 'Margin trading' },
              { key: 'newMintBurnCount', name: 'Mint & Burn' },
              {
                key: 'cumulativeNewUserCount',
                name: 'Cumulative',
                type: 'Line',
                yAxisId: 'right',
                strokeWidth: 2,
                color: LIGHT_ORANGE,
              },
            ]}
            type="Composed"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            syncId="syncPlp"
            loading={usersLoading}
            title="New vs. Existing Users"
            data={usersData?.map((item) => ({
              ...item,
              all: item.uniqueCount,
            }))}
            truncateYThreshold={7000}
            yaxisDataKey="uniqueCount"
            rightYaxisDataKey="oldPercent"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'newCount', name: 'New' },
              { key: 'oldCount', name: 'Existing' },
              {
                key: 'oldPercent',
                name: 'Existing %',
                yAxisId: 'right',
                type: 'Line',
                strokeWidth: 2,
                color: COLORS[4],
                unit: '%',
              },
            ]}
            type="Composed"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            syncId="syncPlp"
            loading={usersLoading}
            title="User Actions"
            data={(usersData || []).map((item) => ({
              ...item,
              all: item.actionCount,
            }))}
            truncateYThreshold={25000}
            yaxisDataKey="actionCount"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'actionSwapCount', name: 'Swaps' },
              { key: 'actionMarginCount', name: 'Margin trading' },
              { key: 'actionMintBurnCount', name: 'Mint & Burn PLP' },
            ]}
            type="Composed"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={swapSourcesLoading}
            title="Swap Sources"
            data={swapSources}
            items={swapSourcesKeys.map((key) => ({ key }))}
          />
        </div>
      </div>
    </div>
  )
}

export default Polygon
